export type OpportunityScheduleFields = {
  hasTime?: boolean | null;
  hasStartTime?: boolean | null;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  timeDescription?: string | null;
};

const NO_SCHEDULE = "ללא זמן מוגדר";

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(d: Date): string {
  return d.toLocaleString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function usesTimeDescription(opp: OpportunityScheduleFields): boolean {
  if (opp.hasTime === false) return true;
  if (opp.hasTime === true) return false;
  // Legacy/partial payloads without hasTime: infer from dates.
  return !opp.startDate && !opp.endDate;
}

/** תיאור זמן כשאין מועד ספציפי */
export function formatOpportunityTimeDescription(
  opp: OpportunityScheduleFields,
): string {
  const desc = (opp.timeDescription ?? "").trim();
  return desc || NO_SCHEDULE;
}

/** תצוגת מועד מלאה — תואם ל-DateAndHoursRange / EventDetails */
export function formatOpportunitySchedule(
  opp: OpportunityScheduleFields,
): string {
  if (usesTimeDescription(opp)) {
    return formatOpportunityTimeDescription(opp);
  }
  if (opp.hasStartTime === false) {
    const end = toDate(opp.endDate);
    return end ? `לביצוע עד ${formatDateTime(end)}` : NO_SCHEDULE;
  }
  const start = toDate(opp.startDate);
  const end = toDate(opp.endDate);
  if (start && end) {
    return isSameDay(start, end)
      ? `${formatDateTime(start)} – ${formatTime(end)}`
      : `${formatDateTime(start)} – ${formatDateTime(end)}`;
  }
  if (start) return formatDateTime(start);
  return NO_SCHEDULE;
}

/** תאריך לתג יומן / לוח שנה; undefined כשיש רק תיאור זמן */
export function getOpportunityScheduleDate(
  opp: OpportunityScheduleFields,
): Date | undefined {
  if (usesTimeDescription(opp)) return undefined;
  if (opp.hasStartTime === false) {
    return toDate(opp.endDate) ?? undefined;
  }
  return toDate(opp.startDate) ?? undefined;
}

/** עמודת התחלה בטבלאות עם שדות startDate/endDate נפרדים */
export function formatOpportunityStartDisplay(
  opp: OpportunityScheduleFields,
): string {
  if (usesTimeDescription(opp)) return formatOpportunityTimeDescription(opp);
  if (opp.hasStartTime === false) return "-";
  const start = toDate(opp.startDate);
  return start ? formatDateTime(start) : "-";
}

/** עמודת סיום בטבלאות עם שדות startDate/endDate נפרדים */
export function formatOpportunityEndDisplay(
  opp: OpportunityScheduleFields,
): string {
  if (usesTimeDescription(opp)) return "-";
  const end = toDate(opp.endDate);
  if (!end) return "-";
  return opp.hasStartTime === false ? `לביצוע עד ${formatDateTime(end)}` : formatDateTime(end);
}
